import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ArrowLeft, Calendar, User, Share2, Facebook, Twitter } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const HERO_BG = "https://images.unsplash.com/photo-1558465202-92356bf74344?crop=entropy&cs=srgb&fm=jpg&q=85";

const Chip = ({ to, testId, children }) => (
  <Link to={to} className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 hover:border-[#009E60] hover:text-[#009E60] text-sm font-medium transition-colors" data-testid={testId}>
    {children}
  </Link>
);

const LinkedEntities = ({ article }) => {
  const { related_teams = [], related_players = [], related_matches = [], related_competitions = [] } = article;
  if (!related_teams.length && !related_players.length && !related_matches.length && !related_competitions.length) return null;
  return (
    <div className="mb-8 p-5 bg-gray-50 border-l-4 border-[#FFC72C]" data-testid="article-linked-entities">
      <h4 className="font-heading font-bold uppercase text-sm text-gray-500 mb-3">In this story</h4>
      <div className="flex flex-wrap gap-2">
        {related_matches.map(m => (
          <Chip key={m.match_id} to={`/matches/${m.match_id}`} testId={`linked-match-${m.match_id}`}>
            {m.home_team_name} {m.status === 'completed' ? `${m.home_score}-${m.away_score}` : 'vs'} {m.away_team_name}
          </Chip>
        ))}
        {related_teams.map(t => (
          <Chip key={t.team_id} to={`/teams/${t.team_id}`} testId={`linked-team-${t.team_id}`}>
            {t.logo_url && <img src={t.logo_url} alt="" className="w-5 h-5 object-contain" />}{t.name}
          </Chip>
        ))}
        {related_players.map(p => (
          <Chip key={p.player_id} to={`/players/${p.player_id}`} testId={`linked-player-${p.player_id}`}>{p.name}</Chip>
        ))}
        {related_competitions.map(c => (
          <Chip key={c.competition_id} to={`/competitions/${c.competition_id}`} testId={`linked-competition-${c.competition_id}`}>{c.name}</Chip>
        ))}
      </div>
    </div>
  );
};

const NewsDetailPage = () => {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articleRes, newsRes] = await Promise.all([
          axios.get(`${API}/news/${articleId}`),
          axios.get(`${API}/news?limit=6`)
        ]);

        setArticle(articleRes.data);
        // Filter out current article from related
        const a = articleRes.data;
        const linkedTeams = new Set(a.related_team_ids || []);
        const others = newsRes.data.filter(n => n.article_id !== a.article_id);
        // Prefer articles that share a linked team, then fall back to most recent
        others.sort((x, y) => (y.related_team_ids || []).filter(t => linkedTeams.has(t)).length - (x.related_team_ids || []).filter(t => linkedTeams.has(t)).length);
        setRelatedNews(others.slice(0, 3));
      } catch (error) {
        console.error("Error fetching article:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [articleId]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getCategoryBadge = (category) => {
    const styles = {
      news: "bg-[#009E60] text-white",
      match_report: "bg-[#FFC72C] text-black",
      press_release: "bg-gray-500 text-white"
    };
    const labels = {
      news: "News",
      match_report: "Match Report",
      press_release: "Press Release"
    };
    return (
      <span className={`px-3 py-1 text-sm font-bold uppercase ${styles[category] || styles.news}`}>
        {labels[category] || category}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-10 w-64 mb-8" />
          <Skeleton className="aspect-video w-full mb-8" />
          <Skeleton className="h-8 w-3/4 mb-4" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold text-gray-400">Article Not Found</h2>
          <Link to="/news" className="text-[#009E60] hover:underline mt-4 inline-block">
            Back to News
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="news-detail-page">
      {/* Hero Image */}
      <section 
        className="relative h-[50vh] bg-cover bg-center"
        style={{ backgroundImage: `url(${article.image_url || HERO_BG})` }}
      >
        <div className="absolute inset-0 hero-overlay"></div>
      </section>

      {/* Content */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link to="/news" className="inline-flex items-center text-gray-500 hover:text-[#009E60] mb-8" data-testid="back-to-news">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to News
          </Link>

          {/* Article Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              {getCategoryBadge(article.category)}
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase text-[#09090b] mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-gray-500">
              <span className="flex items-center gap-1" data-testid="article-published-date">
                <Calendar className="w-4 h-4" />
                {formatDate(article.published_at)}
              </span>
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {article.author || 'ABFA'}
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none mb-12" data-testid="article-content">
            {article.content.split('\n').map((paragraph, i) => (
              <p key={i} className="text-gray-700 leading-relaxed mb-4">
                {paragraph}
              </p>
            ))}
          </div>

          <LinkedEntities article={article} />

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="mb-8">
              <h4 className="font-heading font-bold uppercase text-sm text-gray-500 mb-3">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag, i) => (
                  <span key={i} className="px-3 py-1 bg-gray-100 text-gray-600 text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Share */}
          <div className="flex items-center gap-4 py-8 border-t border-b border-gray-200">
            <span className="font-heading font-bold uppercase text-sm text-gray-500">Share:</span>
            <button className="p-2 bg-blue-600 text-white hover:bg-blue-700 transition-colors" data-testid="share-facebook">
              <Facebook className="w-5 h-5" />
            </button>
            <button className="p-2 bg-sky-500 text-white hover:bg-sky-600 transition-colors" data-testid="share-twitter">
              <Twitter className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Related News */}
      {relatedNews.length > 0 && (
        <section className="py-12 px-4 md:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl font-bold uppercase text-[#09090b] mb-8">Related News</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedNews.map(news => (
                <Link 
                  key={news.article_id} 
                  to={`/news/${news.slug || news.article_id}`}
                  className="card-abfa group"
                  data-testid={`related-news-${news.article_id}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={news.image_url || HERO_BG} 
                      alt={news.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-[#009E60] uppercase tracking-wider mb-2">{news.category}</p>
                    <h3 className="font-heading font-bold uppercase text-[#09090b] group-hover:text-[#009E60] transition-colors">
                      {news.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default NewsDetailPage;
